import type { TauxTvaQuittanceLoyer, TypeBailleur } from "@/types/loyer";

/** TVA 18 % sur loyer (quittance) — uniquement pour rétro-découpage TTC → HT/TVA. */
export const TAUX_TVA_QUITTANCE_LOYER = 0.18;

/** À partir du loyer TTC (avant RSL), déduit HT et TVA (18 %) — anciennes quittances sans HT saisi. */
export function repartirMontantQuittanceLoyerTtc(ttc: number): { montant_ht: number; montant_tva: number } {
  if (ttc <= 0) return { montant_ht: 0, montant_tva: 0 };
  const montant_ht = Math.round(ttc / (1 + TAUX_TVA_QUITTANCE_LOYER));
  const montant_tva = Math.max(0, ttc - montant_ht);
  return { montant_ht, montant_tva };
}

/**
 * Taux RSL (décimal) selon le type de bailleur — réf. CGI Côte d’Ivoire, retenues sur loyers (12 % / 15 % / 0 %).
 */
export function getTauxRSL(typeBailleur: TypeBailleur): number {
  switch (typeBailleur) {
    case "particulier":
      return 0.12;
    case "entreprise":
      return 0.15;
    case "societe_is":
      return 0;
    default:
      return 0.12;
  }
}

/**
 * Quittance : HT saisi, TVA 0 % ou 18 % sur HT, TTC = HT + TVA,
 * RSL sur le HT uniquement (12 % / 15 % / 0 %), net bailleur = TTC − RSL.
 */
export function calculerMontantsQuittanceDepuisHt(
  montantHt: number,
  tauxTvaPct: TauxTvaQuittanceLoyer,
  typeBailleur: TypeBailleur
): {
  montantHt: number;
  montantTva: number;
  montantBrut: number;
  tauxTvaPct: TauxTvaQuittanceLoyer;
  tauxRSL: number;
  montantRSL: number;
  montantNet: number;
} {
  const ht = Math.max(0, Math.round(Number(montantHt) || 0));
  const tvaDec = tauxTvaPct === 18 ? TAUX_TVA_QUITTANCE_LOYER : 0;
  const montantTva = Math.round(ht * tvaDec);
  const montantBrut = ht + montantTva;
  const tauxRslDec = getTauxRSL(typeBailleur);
  const montantRSL = Math.round(ht * tauxRslDec);
  const montantNet = montantBrut - montantRSL;
  return {
    montantHt: ht,
    montantTva,
    montantBrut,
    tauxTvaPct,
    tauxRSL: tauxRslDec * 100,
    montantRSL,
    montantNet,
  };
}

export function calculerRSL(
  montantSaisi: number,
  typeBailleur: TypeBailleur,
  estMontantNet: boolean
): {
  montantBrut: number;
  tauxRSL: number;
  montantRSL: number;
  montantNet: number;
} {
  const taux = getTauxRSL(typeBailleur);

  let montantBrut: number;
  let montantNet: number;
  let montantRSL: number;

  if (estMontantNet) {
    montantNet = Math.round(montantSaisi);
    montantBrut = taux === 0 ? montantNet : Math.round(montantNet / (1 - taux));
    montantRSL = montantBrut - montantNet;
  } else {
    montantBrut = Math.round(montantSaisi);
    montantRSL = Math.round(montantBrut * taux);
    montantNet = montantBrut - montantRSL;
  }

  return {
    montantBrut,
    tauxRSL: taux * 100,
    montantRSL,
    montantNet,
  };
}

/** 15 du mois suivant la date de quittance (AAAA-MM-JJ). */
export function getDateEcheanceRSL(dateQuittance: string): string {
  const s = dateQuittance.slice(0, 10);
  const parts = s.split("-");
  const y = parseInt(parts[0] || "0", 10);
  const mo = parseInt(parts[1] || "1", 10);
  if (!y || !mo) return s;
  const echeance = new Date(y, mo, 15);
  const yy = echeance.getFullYear();
  const mm = String(echeance.getMonth() + 1).padStart(2, "0");
  const dd = String(echeance.getDate()).padStart(2, "0");
  return `${yy}-${mm}-${dd}`;
}

export function getLibelleTypeBailleur(type: TypeBailleur): string {
  switch (type) {
    case "particulier":
      return "Particulier (12 %)";
    case "entreprise":
      return "Entreprise / personne morale (15 %)";
    case "societe_is":
      return "Société IS au régime réel (0 %)";
    default:
      return "Non défini";
  }
}
